import IcDelete from '@assets/details-camera-icons/IcDelete';
import IcEditGeoZone from '@assets/details-camera-icons/IcEditGeoZone';
import BaseIcon from '@components/BaseIcon';
import { BaseHead, CellOfRow } from '@components/tables';
import { GeoZones, Status } from '@EcamModel/model';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import color from '@theme/Colors';
import { convertSecondsToDate } from '@utils/functions';
import { formatSecondsToHHMMSS } from '../../../utils';
import { groupBy } from 'lodash';
import moment from 'moment';
import { formatUKDate } from 'src/helpers';

interface BaseTableProps {
    rows: GeoZones[];
    cameraDeleted: boolean;
    showViewName?: boolean;
    locationName?: string;
    zoneName?: string;
    handleEdit?: (item: GeoZones) => void;
    handleDelete?: (item: GeoZones) => void;
}

const BaseTableGeoZonesDetail: React.FC<BaseTableProps> = (props) => {
    const { rows, cameraDeleted, showViewName, locationName, zoneName, handleEdit, handleDelete } = props;
    const theme = useTheme();
    return (
        <TableContainer component={Box} mt={2}>
            {!rows.length ? (
                <ListNoData />
            ) : (
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <BaseHead align="center" title="Geo-zone" />
                            {showViewName && <BaseHead align="center" title="View" />}
                            <BaseHead align="left" title="Name" />
                            <BaseHead align="left" title="Current operation hours" />
                            <BaseHead align="left" title="Location" />
                            <BaseHead align="left" title="Zone" />
                            <BaseHead align="center" title="Contravention type" />
                            <BaseHead align="center" title="Status" />
                            {cameraDeleted && <BaseHead align="center" title="" />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((item, index) => {
                            const operationHoursConfigs = item.GZOConfigs?.[0];
                            const operationHours = operationHoursConfigs?.GZOConfigGracePeriods.flatMap((item) =>
                                item.GZOConfigDays.map((item) => item)
                            );

                            const _operationHours = groupBy(operationHours, 'Weekday');

                            return (
                                <TableRow
                                    key={`${index}+1`}
                                    sx={{
                                        verticalAlign: !!operationHoursConfigs?.EffectiveFrom ? 'top' : 'middle',
                                    }}
                                >
                                    <CellOfRow value={index + 1} align="center" />
                                    {showViewName && (
                                        <CellOfRow value={item?.CameraView?.CameraViewName ?? ''} align="center" />
                                    )}
                                    <CellOfRow value={<Typography>{item.GeoZoneName}</Typography>} align="left" />
                                    <CellOfRow
                                        value={
                                            <Stack>
                                                {!!operationHoursConfigs?.EffectiveFrom ? (
                                                    <Typography color="#256428">
                                                        Effective from:{' '}
                                                        {formatUKDate(operationHoursConfigs?.EffectiveFrom)}
                                                    </Typography>
                                                ) : (
                                                    <Typography fontStyle="italic" color={theme.palette.grey[600]}>
                                                        No current operation hours
                                                    </Typography>
                                                )}
                                                {Object.entries(_operationHours).map(([key, value], index) => (
                                                    <Stack
                                                        direction="row"
                                                        alignItems="flex-start"
                                                        spacing={1}
                                                        mb={1}
                                                        key={key}
                                                    >
                                                        <Typography minWidth={80}>
                                                            {moment().isoWeekday(parseInt(key)).format('dddd')}
                                                        </Typography>
                                                        <Stack>
                                                            {value.map((o) => {
                                                                const geoZoneConfig =
                                                                    operationHoursConfigs?.GZOConfigGracePeriods.find(
                                                                        (p) =>
                                                                            p.Id ===
                                                                            o.GeoZoneOperationConfigGracePeriodId
                                                                    );
                                                                return (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        spacing={0.5}
                                                                        pr={2}
                                                                        key={o.GeoZoneOperationConfigGracePeriodId}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                p: 0.4,
                                                                                background: '#256428',
                                                                                borderRadius: 1,
                                                                            }}
                                                                        />
                                                                        <Typography color="GrayText">
                                                                            {moment(
                                                                                convertSecondsToDate(o.From!)
                                                                            ).format('HH:mm')}
                                                                            -
                                                                            {moment(convertSecondsToDate(o.To!)).format(
                                                                                'HH:mm'
                                                                            )}{' '}
                                                                            | Grace period:{' '}
                                                                            {geoZoneConfig?.GracePeriod != null ? formatSecondsToHHMMSS(geoZoneConfig?.GracePeriod) : '-'}
                                                                        </Typography>
                                                                    </Stack>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        }
                                        align="left"
                                    />
                                    <CellOfRow
                                        value={
                                            <Box
                                                sx={{
                                                    padding: '2px 8px',
                                                    borderRadius: 1,
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <Typography fontSize={'14px'}>
                                                    {item.Zone?.Location?.Name ?? locationName}
                                                </Typography>
                                            </Box>
                                        }
                                        align="left"
                                    />
                                    <CellOfRow
                                        value={
                                            <Box
                                                sx={{
                                                    padding: '2px 8px',
                                                    borderRadius: 1,
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Typography fontSize={'14px'}>
                                                        {item.Zone?.PublicName ?? zoneName}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        }
                                        align="left"
                                    />
                                    <CellOfRow
                                        value={item.ContraventionReason?.ContraventionReasonTranslations?.[0]?.Summary!}
                                        align="center"
                                    />
                                    <CellOfRow
                                        value={
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="center"
                                                spacing={1}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 5,
                                                        height: 5,
                                                        background:
                                                            item.Status === Status.Active ? '#2E7D32' : '#E01B00',
                                                        borderRadius: '100%',
                                                        animation: 'blinker 2s linear infinite',
                                                        '@keyframes blinker': {
                                                            '0%': {
                                                                opacity: 1,
                                                            },
                                                            '50%': {
                                                                opacity: 0.5,
                                                            },
                                                            '100%': {
                                                                opacity: 1,
                                                            },
                                                        },
                                                    }}
                                                />
                                                <Typography>
                                                    {item.Status === Status.Active ? 'Active' : 'Inactive'}
                                                </Typography>
                                            </Stack>
                                        }
                                        align="center"
                                    />
                                    {cameraDeleted && (
                                        <CellOfRow
                                            value={
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    spacing={1}
                                                >
                                                    {handleEdit && (
                                                        <BaseIcon
                                                            title="Edit"
                                                            colorHover={theme.palette.secondary.main}
                                                            children={<IcEditGeoZone />}
                                                            onClick={() => handleEdit(item)}
                                                        />
                                                    )}
                                                    {handleDelete && (
                                                        <BaseIcon
                                                            isDelete
                                                            colorHover={theme.palette.error.main}
                                                            children={<IcDelete />}
                                                            onClick={() => handleDelete(item)}
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            align="center"
                                        />
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </TableContainer>
    );
};

export default BaseTableGeoZonesDetail;
